import { useCallback, useEffect, useMemo, useState } from "react";
import { hasRole, useApi, useApp } from "../context/AppContext";

// The cloud classifier (docs/tasks/38): which model the cloud check asks now,
// what it knows and what it does not, the language model beside it, and its
// retraining - on a schedule or on the button here. A run trains on a GPU VM;
// the running model stays until a new one proves not worse on held-out
// purchases.

const STATUS = {
  queued: { label: "Queued", cls: "bg-slate-100 text-slate-700" },
  starting: { label: "Starting the VM", cls: "bg-sky-100 text-sky-800" },
  preparing: { label: "Preparing data", cls: "bg-sky-100 text-sky-800" },
  training: { label: "Training", cls: "bg-sky-100 text-sky-800" },
  evaluating: { label: "Evaluating", cls: "bg-sky-100 text-sky-800" },
  deploying: { label: "Deploying", cls: "bg-sky-100 text-sky-800" },
  deployed: { label: "Deployed", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Kept the old model", cls: "bg-amber-100 text-amber-800" },
  failed: { label: "Failed", cls: "bg-rose-100 text-rose-800" },
};
const ACTIVE = new Set(["queued", "starting", "preparing", "training", "evaluating", "deploying"]);
const POLL_MS = 15000;

// Run times are epoch seconds (floats); the deployment time is ISO text.
function toDate(v) {
  if (v == null || v === "") return null;
  const d = typeof v === "number" || /^\d+(\.\d+)?$/.test(String(v)) ? new Date(Number(v) * 1000) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function when(v) {
  if (v == null || v === "") return "—";
  const d = toDate(v);
  return d ? d.toLocaleString() : String(v);
}

function ago(v) {
  const d = toDate(v);
  if (!d) return "";
  const t = d.getTime();
  const m = Math.round((Date.now() - t) / 60000);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function pct(x) {
  return x == null ? "—" : `${(x * 100).toFixed(1)}%`;
}

function Badge({ status }) {
  const s = STATUS[status] || { label: status || "—", cls: "bg-slate-100 text-slate-700" };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function byWhom(run, people) {
  if (run.trigger === "schedule") return "schedule";
  const who = run.requested_by;
  if (!who) return "operator";
  return people[who] || (/^[0-9a-f-]{32,36}$/i.test(who) ? "operator" : who);
}

function RunRow({ run, names, people }) {
  const [open, setOpen] = useState(false);
  const m = run.metrics || {};
  const fresh = m.new?.new_classes || {};
  const classes = useMemo(() => {
    const keys = new Set([...Object.keys(m.old?.per_class || {}), ...Object.keys(m.new?.per_class || {})]);
    return [...keys].sort((a, b) => (names[a] || a).localeCompare(names[b] || b));
  }, [m, names]);
  return (
    <>
      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)}>
        <td className="text-xs">{when(run.requested_at)}</td>
        <td className="max-w-xs">
          <Badge status={run.status} />
          {run.phase ? <span className={`ml-1 text-xs ${run.status === "failed" ? "text-rose-700" : "text-slate-500"}`} title={run.phase}>{run.phase.length > 70 ? `${run.phase.slice(0, 70)}…` : run.phase}</span> : null}
        </td>
        <td className="text-xs">{byWhom(run, people)}</td>
        <td className="text-xs">{run.reason || "—"}</td>
        <td className="font-mono text-xs">{m.old || m.new ? `${pct(m.old?.acc)} → ${pct(m.new?.acc)}` : "—"}</td>
        <td className="font-mono text-xs">{run.tag || "—"}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="bg-slate-50 px-3 py-2 text-xs">
            <div className="grid gap-1 md:grid-cols-2">
              <div>Started {when(run.started_at)} · finished {when(run.finished_at)}</div>
              <div>Decision: {run.decision === "deploy" ? "deployed the new model" : run.decision === "keep" ? "kept the running model" : "—"}</div>
              {run.data && (
                <div className="md:col-span-2">
                  Data: {run.data.classes ?? "—"} classes
                  {run.data.purchase_crops != null ? ` · ${run.data.purchase_crops} purchase crops` : run.data.new_crops != null ? ` · ${run.data.new_crops} new crops` : ""}
                  {run.data.holdout_crops != null ? ` · hold-out ${run.data.holdout_crops} crops` : run.data.holdout != null ? ` · hold-out ${run.data.holdout}` : ""}
                  {run.data.purchases_by_product ? <> · purchases: {Object.entries(run.data.purchases_by_product).map(([k, v]) => `${names[k] || k.slice(0, 8)} ${v}`).join(", ")}</> : null}
                  {run.data.crops_by_class ? <> · {Object.entries(run.data.crops_by_class).map(([k, v]) => `${names[k] || k.slice(0, 8)} ${v}`).join(", ")}</> : null}
                </div>
              )}
              {(m.old || m.new) && (
                <div className="md:col-span-2">
                  Hold-out: old {pct(m.old?.acc)}{m.old?.n != null ? ` on ${m.old.n}` : ""} · new {pct(m.new?.acc)}{m.new?.n != null ? ` on ${m.new.n}` : ""}
                  {fresh.acc != null ? ` · new classes ${pct(fresh.acc)}${fresh.n != null ? ` on ${fresh.n}` : ""}` : ""}
                  {m.why ? <span className="text-slate-600"> - {m.why}</span> : null}
                </div>
              )}
              {run.phase && <div className="md:col-span-2">Phase: <span className={run.status === "failed" ? "text-rose-700" : ""}>{run.phase}</span></div>}
              {run.error && <div className="whitespace-pre-wrap break-words text-rose-700 md:col-span-2">Error: {run.error}</div>}
            </div>
            {classes.length > 0 && (
              <table className="mt-2 text-xs">
                <thead><tr className="text-slate-500"><th className="pr-4 text-left">Class</th><th className="pr-4 text-right">Old</th><th className="text-right">New</th></tr></thead>
                <tbody>
                  {classes.map((k) => {
                    const o = m.old?.per_class?.[k]; const n = m.new?.per_class?.[k];
                    const worse = o != null && n != null && n < o - 0.01;
                    const cnt = m.new?.counts?.[k] ?? m.old?.counts?.[k];
                    return (
                      <tr key={k} className={worse ? "text-rose-700" : ""}>
                        <td className="pr-4">{names[k] || k}{cnt != null ? <span className="text-slate-400"> ({cnt})</span> : null}</td><td className="pr-4 text-right font-mono">{pct(o)}</td><td className="text-right font-mono">{pct(n)}</td>
                      </tr>
                    );
                  })}
                  {Object.entries(fresh.per_class || {}).map(([k, v]) => (
                    <tr key={`new-${k}`} className={v < 0.8 ? "text-rose-700" : "text-emerald-800"}>
                      <td className="pr-4">{names[k] || k} <span className="text-slate-400">new{fresh.counts?.[k] != null ? `, ${fresh.counts[k]}` : ""}</span></td><td className="pr-4 text-right font-mono">—</td><td className="text-right font-mono">{pct(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function ClassifierPage() {
  const api = useApi();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState(null);
  const mayRun = hasRole(user, "tenant_admin", "ml_operator");
  // Who asked for a run: the tenant's users, for an admin who may list them.
  const [people, setPeople] = useState({});
  useEffect(() => {
    if (!hasRole(user, "tenant_admin")) return;
    api.listUsers().then((list) => {
      const out = {};
      for (const u of (list && list.users) || list || []) out[u.id || u.user_id] = u.display_name || u.name || u.email;
      setPeople(out);
    }).catch(() => {});
  }, [api, user]);

  const load = useCallback(() => api.getClassifier().then((d) => { setData(d); setErr(null); })
    .catch((e) => setErr(e.status === 404 && e.code === "http_404" ? "The classifier status is not deployed on the backend yet." : String(e.message || e))), [api]);
  useEffect(() => { load(); }, [load]);
  const active = data?.active_run && ACTIVE.has(data.active_run.status) ? data.active_run : null;
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [active, load]);

  const names = useMemo(() => {
    const out = {};
    // Run metrics key classes by the first 8 characters of the product id.
    const put = (id, name) => { if (id) { out[id] = name; out[String(id).slice(0, 8)] = name; } };
    for (const c of data?.current?.classes || []) put(c.product_uuid, c.product_name || c.ean_code || c.product_uuid);
    for (const p of data?.untrained_products || []) put(p.product_uuid, p.product_name || p.product_uuid);
    out.hand = "hand";
    return out;
  }, [data]);

  const retrain = async () => {
    if (!window.confirm("Retrain the cloud classifier?\n\nAbout 70 minutes on a GPU VM, under $1. The current model keeps running; "
      + "the new one is deployed only if it is not worse on held-out purchases.")) return;
    setBusy(true); setMsg(null);
    try {
      await api.startClassifierRun(reason.trim() || "manual retrain from the admin");
      setReason("");
      setMsg({ text: "Run queued." });
      await load();
    } catch (e) {
      setMsg({ bad: true, text: e.code === "run_active" ? "A run is already in progress." : String(e.message || e) });
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="space-y-2"><h1 className="text-xl font-semibold">Cloud classifier</h1><p className="text-sm text-rose-700">{err}</p></div>;
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const cur = data.current || {};
  const classes = cur.classes || [];
  const fam = data.families || [];
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Cloud classifier</h1>
        <p className="text-sm text-slate-500">The second opinion of the cloud check for look-alike products and weak lines (handbook ch. 8, docs/tasks/38). A product it does not know is not sent to it.</p>
      </div>

      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Running now</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">Model</dt>
          <dd><span className="font-mono">{cur.tag || "unknown"}</span>{cur.deployed_at ? <span className="text-xs text-slate-500"> · deployed {when(cur.deployed_at)} ({ago(cur.deployed_at)})</span> : null}</dd>
          <dt className="text-slate-500">Knows</dt>
          <dd className="text-sm">
            {classes.length ? classes.map((c) => c.product_name || c.ean_code || c.product_uuid).join(", ") : "—"}
            {cur.hand_class ? <span className="text-xs text-slate-500"> · plus a “hand” class (an empty hand, not a product)</span> : null}
          </dd>
          <dt className="text-slate-500">Does not know</dt>
          <dd className="text-sm">
            {(data.untrained_products || []).length
              ? <span className="text-amber-800">{data.untrained_products.map((p) => p.product_name || p.product_uuid).join(", ")}</span>
              : <span className="text-emerald-700">every product in the layouts</span>}
          </dd>
          <dt className="text-slate-500">Look-alike families</dt>
          <dd className="text-xs">{fam.length ? fam.map((f, i) => <div key={i}>{f.map((u) => names[u] || u.slice(0, 8)).join(" · ")}</div>) : "none"}</dd>
          <dt className="text-slate-500">Language model</dt>
          <dd className="text-xs">{data.vlm?.deployment || "—"} · {data.vlm?.may_act ? "may correct carts when the classifier cannot" : "shadow only"}</dd>
          <dt className="text-slate-500">Service</dt>
          <dd className="text-xs">{cur.service_host || "—"}{cur.fallback_model_id ? ` · fallback ${cur.fallback_model_id}` : ""}</dd>
          <dt className="text-slate-500">Schedule</dt>
          <dd className="text-xs">{data.schedule?.next_at ? `next ${when(data.schedule.next_at)}` : "—"}{data.schedule?.cron ? <span className="font-mono text-slate-500"> ({data.schedule.cron}, UTC)</span> : null}</dd>
        </dl>
      </div>

      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Retraining</h2>
        {active ? (
          <div className="space-y-1 text-sm">
            <div><Badge status={active.status} /> {active.phase ? <span className="text-xs text-slate-500">{active.phase}</span> : null}</div>
            <p className="text-xs text-slate-600">Requested {when(active.requested_at)} by {active.trigger === "schedule" ? "the schedule" : byWhom(active, people)}{active.reason ? ` - ${active.reason}` : ""}. This page refreshes every 15 s.</p>
            {active.status === "queued" && String(active.phase || "").startsWith("waiting: the training VM is not configured") ? (
              <p className="text-xs text-amber-800">Queued: nothing runs it yet - the training VM is not configured. The run stays here until it is; the running model is not affected.</p>
            ) : active.status === "queued" ? (
              <p className="text-xs text-slate-600">Queued: the scheduler starts the training VM within about 5 minutes. The running model is not affected.</p>
            ) : null}
          </div>
        ) : mayRun ? (
          <div className="flex flex-wrap items-center gap-2">
            <input className="input flex-1 text-sm" placeholder="Why (e.g. a new milk joined the cartons family)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button className="btn btn-primary text-sm" disabled={busy} onClick={retrain}>Retrain classifier</button>
          </div>
        ) : <p className="text-xs text-slate-500">A tenant admin or an ML operator can start a retrain.</p>}
        {msg && <p className={`mt-2 text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</p>}
        <p className="mt-2 text-[11px] text-slate-500">A run builds the data from labelled purchase crops, trains on a GPU VM (~70 min, under $1), compares old and new on held-out purchases and deploys only when the new one is not worse; otherwise it keeps the running model.</p>
      </div>

      <div className="card overflow-x-auto">
        <h2 className="mb-2 text-sm font-semibold">Runs</h2>
        {(data.runs || []).length === 0 ? <p className="text-sm text-slate-500">No runs yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500"><th>Requested</th><th>Status</th><th>By</th><th>Why</th><th>Accuracy old → new</th><th>Model</th></tr></thead>
            <tbody>{data.runs.map((r) => <RunRow key={r.id} run={r} names={names} people={people} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
